import React from "react";
import { Link } from "react-router-dom";
import { useBilling } from "@/contexts/BillingContext";
import { useTranslation } from "react-i18next";
import { BILLING } from "@/constants/testIds";
import { useL } from "@/lib/useL";
import { Lock, Sparkles, Check, ExternalLink, X } from "lucide-react";

export const UsageMeter = () => {
  const { billing, paymentsBlockedInTWA } = useBilling();
  const { t } = useTranslation();
  const L = useL();
  if (!billing) return null;
  if (billing.is_pro) {
    return (
      <div
        data-testid={BILLING.proBadge}
        className="inline-flex items-center gap-2 border border-[#002FA7] bg-[#002FA7] text-white px-3 py-1.5 font-mono uppercase tracking-[0.2em] text-[10px]"
      >
        <Sparkles className="w-3 h-3" /> SUX1 Pro
      </div>
    );
  }
  if (billing.is_admin) {
    return (
      <div data-testid="admin-unlimited-usage" className="border border-[#002FA7] bg-[#002FA7] text-white p-4 font-mono uppercase tracking-[0.2em] text-[11px] inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3" /> {t("trial.usage_admin")}
      </div>
    );
  }
  if (billing.credits > 0) {
    return (
      <Link
        to="/billing/upgrade"
        data-testid={BILLING.banner}
        className="border border-border bg-card p-4 flex items-center justify-between gap-4 font-mono uppercase tracking-[0.2em] text-[11px] hover:bg-[#002FA7]/5"
      >
        <span data-testid={BILLING.usageCounter}>{t("billing.credits_balance", { count: billing.credits })}</span>
        <span className="text-[#002FA7]">{t("billing.upgrade_now")}</span>
      </Link>
    );
  }
  const used = billing.free_summaries_used;
  const total = billing.free_summary_limit;
  const remaining = Math.max(0, total - used);
  const pct = Math.min(100, Math.round((used / total) * 100));
  const isAnon = !!billing.is_anonymous;

  if (billing.free_trial_open === false) {
    const price = (billing.packs?.[0]?.amount ?? 2).toFixed(0);
    return (
      <Link to={isAnon ? "/signup" : "/billing/upgrade"} data-testid={BILLING.banner}
        className="border border-border bg-card p-4 flex items-center justify-between gap-4 flex-wrap font-mono uppercase tracking-[0.14em] text-[11px] hover:bg-[#002FA7]/5">
        <span data-testid="no-trial-banner-text">
          {L(`Exemples gratuits ci-dessous · Résumer votre texte : dès ${price} € les 20 résumés`, `Free examples below · Summarise your own text: from €${price} for 20 summaries`)}
          {billing.discovery_pack && <b className="text-[#FF2A00]" data-testid="no-trial-banner-discovery">{L(" · Pack découverte : 1 € les 10", " · Discovery pack: €1 for 10")}</b>}
        </span>
        <span className="text-[#002FA7]">{isAnon ? L("Créer un compte", "Create an account") : t("billing.upgrade_now")}</span>
      </Link>
    );
  }

  return (
    <div
      data-testid={BILLING.banner}
      className="border border-border bg-card p-4 flex items-center gap-4 flex-wrap"
    >
      <div className="flex-1 min-w-[180px]">
        <div
          data-testid={BILLING.usageCounter}
          className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground mb-1.5"
        >
          {isAnon
            ? t("billing.anon_free_counter", {
                remaining,
                total,
                defaultValue: "Essai gratuit sans compte · {{remaining}}/{{total}}",
              })
            : t("billing.free_trial_counter", { remaining, total })}
        </div>
        <div className="h-1.5 w-full bg-secondary relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#002FA7]"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <Link
        to={isAnon ? "/signup" : "/billing/upgrade"}
        data-testid={BILLING.bannerUpgradeBtn}
        className="font-mono uppercase tracking-[0.25em] text-[11px] bg-[#002FA7] text-white px-4 py-2.5 hover:bg-[#00227A] transition-colors flex items-center gap-2"
      >
        {isAnon ? (
          <>
            <Sparkles className="w-3 h-3" />
            {t("billing.anon_signup_cta", "Créer un compte")}
          </>
        ) : paymentsBlockedInTWA ? (
          <>
            <ExternalLink className="w-3 h-3" /> {t("billing.upgrade_now")}
          </>
        ) : (
          <>
            <Sparkles className="w-3 h-3" /> {t("billing.upgrade_now")}
          </>
        )}
      </Link>
    </div>
  );
};

export const Paywall = () => {
  const { billing, paymentsBlockedInTWA } = useBilling();
  const { t } = useTranslation();

  if (!billing) return null;
  const pack = billing.packs?.[0] || { amount: 2, credits: 20 };
  const packPrice = pack.amount.toFixed(2).replace(".", ",");

  return (
    <div
      data-testid={BILLING.paywallScreen}
      className="border-t border-border bg-secondary/40 min-h-[70vh] flex items-center justify-center px-6 py-16"
    >
      <div className="max-w-2xl w-full bg-card border border-border p-10 lg:p-14">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-5 h-5 text-[#002FA7]" />
          <div className="sux-marker">{t("billing.paywall_marker")}</div>
        </div>
        <h1
          className="text-4xl md:text-5xl font-black tracking-tight mb-4 leading-[1.0]"
          style={{ fontFamily: "Outfit" }}
        >
          {t("billing.paywall_title")}
        </h1>
        <p className="text-muted-foreground mb-10 text-[15px] leading-relaxed">
          {t("billing.paywall_body", { limit: billing.free_summary_limit })}
        </p>

        <div className="border border-[#002FA7] p-6 lg:p-8 mb-8 bg-card">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h2
              className="text-2xl font-black tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              {t("billing.pack_credits", { count: pack.credits })}
            </h2>
            <div className="text-right">
              <div
                className="text-5xl font-black"
                style={{ fontFamily: "Outfit" }}
              >
                {packPrice} €
              </div>
              <div className="sux-marker mt-1">{t("billing.paywall_one_time")}</div>
            </div>
          </div>

          <ul className="space-y-2.5 text-sm">
            {[
              t("billing.paywall_features_unlimited"),
              t("billing.paywall_features_models"),
              t("billing.paywall_features_share"),
              t("billing.paywall_features_lifetime"),
            ].map((line) => (
              <li key={line} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Anti-objection: "But ChatGPT does it for free" — condensed
            comparison table shown at the exact moment the user weighs
            paying vs walking away. */}
        <div
          data-testid="paywall-vs-chatgpt"
          className="border border-border overflow-x-auto mb-8"
        >
          <div className="px-4 py-3 border-b border-border bg-secondary/40">
            <div className="sux-marker">{t("billing.paywall_vs_marker")}</div>
          </div>
          <table className="w-full text-[13px] border-collapse min-w-[520px]">
            <thead>
              <tr className="bg-[#002FA7] text-white">
                <th className="text-left px-3 py-2 font-mono uppercase tracking-[0.1em] text-[9px] w-[38%]">
                  {t("billing.paywall_vs_feature")}
                </th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-[0.1em] text-[9px] w-[26%]">
                  {t("billing.paywall_vs_chatgpt")}
                </th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-[0.1em] text-[9px] w-[36%] bg-[#002FA7]">
                  {t("billing.paywall_vs_sux1")}
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                ["row_length", "row_length_chatgpt", "row_length_sux1"],
                ["row_pdf", "row_pdf_chatgpt", "row_pdf_sux1"],
                ["row_epub", "row_epub_chatgpt", "row_epub_sux1"],
                ["row_history", "row_history_chatgpt", "row_history_sux1"],
                ["row_ai", "row_ai_chatgpt", "row_ai_sux1"],
                ["row_price", "row_price_chatgpt", "row_price_sux1"],
              ].map(([labelKey, cKey, sKey], idx) => (
                <tr
                  key={labelKey}
                  className={idx % 2 === 0 ? "bg-white" : "bg-secondary/40"}
                >
                  <td className="px-3 py-2 font-semibold align-top border-r border-border">
                    {t(`billing.paywall_vs_${labelKey}`)}
                  </td>
                  <td className="px-3 py-2 align-top border-r border-border text-muted-foreground">
                    <div className="flex items-start gap-1.5">
                      <X className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span>{t(`billing.paywall_vs_${cKey}`)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top bg-[#002FA7]/5">
                    <div className="flex items-start gap-1.5">
                      <Check className="w-3 h-3 text-[#002FA7] mt-0.5 flex-shrink-0" />
                      <span>{t(`billing.paywall_vs_${sKey}`)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Link
          to="/billing/upgrade"
          data-testid={BILLING.paywallUpgradeBtn}
          className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.3em] text-xs py-5 hover:bg-[#00227A] transition-colors flex items-center justify-center gap-3"
        >
          {paymentsBlockedInTWA ? (
            <>
              <ExternalLink className="w-4 h-4" />
              {t("billing.paywall_cta", { price: packPrice })}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              {t("billing.paywall_cta", { price: packPrice })}
            </>
          )}
        </Link>

        <div className="mt-6 sux-marker text-center">
          {paymentsBlockedInTWA
            ? t("billing.paywall_secured_twa")
            : t("billing.paywall_secured")}
        </div>
      </div>
    </div>
  );
};
